// WeatherAlertDetail — the expanded body of the weather banner (#92).
//
// The banner keeps its one-line summary as the collapsed state; this is what
// hovering (desktop) or tapping (mobile) reveals. It answers the three things
// the summary cannot: how many distinct warnings are active and how severe,
// when each one starts and expires, and which regions each one covers.
//
// Regions are only nameable once the deployed Lambda carries the areaDesc
// capture from #56. Until then a warning covering three regions is shown as
// "3 regional warnings — regions not recorded" rather than as one warning:
// hiding the multiplicity would be a quieter lie than admitting the gap.
import React from "react";
import { Box, Typography } from "@mui/material";
import { ALERT_DOT_COLOR, formatAlertTime } from "../weatherAlerts";

function AlertRow({ group, t, compact, last }) {
  const onset = formatAlertTime(group.onset);
  const expires = formatAlertTime(group.expires);

  return (
    <Box sx={{
      display: "flex",
      gap: 1,
      py: compact ? 0.5 : 0.65,
      borderBottom: last ? "none" : "1px solid rgba(0,0,0,0.07)",
    }}>
      {/* Severity is carried by a dot rather than a second colour block, so a
          list of three warnings does not fight the banner's own tint. */}
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        mt: compact ? "5px" : "6px",
        bgcolor: ALERT_DOT_COLOR[group.color] || ALERT_DOT_COLOR.yellow,
      }} />

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: compact ? 12 : 12.5, fontWeight: 700, lineHeight: 1.35,
        }}>
          {group.event}
        </Typography>

        {/* Severity and certainty are Meteoalarm's own vocabulary (Severe,
            Likely, Observed…). They are shown verbatim, not translated — a
            re-worded severity would no longer match the official warning. */}
        <Typography sx={{
          fontSize: compact ? 10.5 : 11, lineHeight: 1.4, opacity: 0.85,
        }}>
          {group.severity} · {group.certainty}
        </Typography>

        <Typography sx={{
          fontSize: compact ? 10.5 : 11, lineHeight: 1.4, opacity: 0.85,
        }}>
          {onset || t("weatherAlertStartUnknown")}
          {" → "}
          {expires || t("weatherAlertNoExpiry")}
        </Typography>

        {group.regions.length > 0 ? (
          <Typography sx={{
            fontSize: compact ? 10.5 : 11, lineHeight: 1.4, opacity: 0.85,
          }}>
            {t("weatherAlertRegions", { list: group.regions.join(", ") })}
          </Typography>
        ) : group.count > 1 ? (
          <Typography sx={{
            fontSize: compact ? 10.5 : 11, lineHeight: 1.4, opacity: 0.7,
            fontStyle: "italic",
          }}>
            {t("weatherAlertRegionsUnrecorded", { n: group.count })}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function WeatherAlertDetail({ groups, t, compact = false, color }) {
  if (!groups || groups.length === 0) return null;

  return (
    <Box sx={{ color, minWidth: 0 }}>
      <Typography sx={{
        fontSize: compact ? 11 : 11.5, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.75,
        pb: 0.5,
      }}>
        {groups.length === 1
          ? t("weatherAlertCountOne")
          : t("weatherAlertCount", { n: groups.length })}
      </Typography>

      {groups.map((group, i) => (
        <AlertRow
          key={`${group.event}-${group.onset}-${group.expires}-${i}`}
          group={group}
          t={t}
          compact={compact}
          last={i === groups.length - 1}
        />
      ))}
    </Box>
  );
}
